/**
 * Custom Hook: useDynamicRoutes
 * Generates and manages dynamic routes based on user RBAC permissions
 *
 * Usage: const { routes, isLoading, allowedRoutes } = useDynamicRoutes();
 *
 * NOTE: The permissions store already merges API data with default config,
 * so routePermissions here is always the merged result.
 */

import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useRoutePermissions } from 'modules/user_management/store/permissionsStore';
import { generateDynamicRoutes, getAllowedRoutes } from './dynamicRouter';

export const useDynamicRoutes = () => {
  const { routePermissions, isLoading, lastUpdated, fetchRoutePermissions } = useRoutePermissions();
  const [routes, setRoutes] = useState([]);
  const [allowedRoutes, setAllowedRoutes] = useState([]);
  const [routesReady, setRoutesReady] = useState(false);

  // Only fetch once there is a session to fetch with. Without a token this
  // 401s on every logged-out page load, which is wasted work at best and, on
  // the pages that sign a user in, actively destructive: the interceptor used
  // to answer that 401 by clearing all cookies and redirecting to /login.
  // Covers the /reset-password case this used to special-case — logged out
  // means no token, so the fetch is skipped there too. Every login path ends
  // in a full reload (setUserDetails -> window.location.href), so the token is
  // always present at mount by the time permissions are needed.
  useEffect(() => {
    if (!Cookies.get('token')) return;
    fetchRoutePermissions();
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // routePermissions is already merged with defaults by the store
    const permissionsToUse = routePermissions;

    if (!permissionsToUse || Object.keys(permissionsToUse).length === 0) {
      setRoutesReady(true);
      return;
    }

    try {
      const generatedRoutes = generateDynamicRoutes(permissionsToUse);
      const allowed = getAllowedRoutes(permissionsToUse);

      setRoutes(generatedRoutes);
      setAllowedRoutes(allowed);
      setRoutesReady(true);
    } catch (error) {
      setRoutes([]);
      setAllowedRoutes([]);
      setRoutesReady(true);
    }
  }, [routePermissions, isLoading, lastUpdated]);

  // Report loading until BOTH API is done AND routes are generated
  const effectiveLoading = isLoading || !routesReady;

  return {
    routes,
    isLoading: effectiveLoading,
    allowedRoutes,
    permissionsLoaded: !effectiveLoading
  };
};

export default useDynamicRoutes;
